import React, { FC, useState } from 'react';
import { Button } from 'antd';

import Modal from '../Modal/Modal';
import api from '../../utils/api.util';
import { IOrderDetails } from '../../interfaces/common';
import { ENDPOINT } from '../../constants/common-app.const';

import css from './PaymentModal.module.css';
import { SVGNewTab } from './OpenInNewTab';
import Spinner from '../Spinner/Spinner';

export interface IErip {
  accountNumber: string;
  invoiceNumber: number;
  amountToPay: number;
  eripQRCodeLink: string;
}

export interface IPaymentModal {
  onClose: () => void;
  isDisplay: boolean;
  tariffId?: string;
  themeId?: string;
  paymentData: string[];
}

const PaymentModal: FC<IPaymentModal> = ({
  onClose,
  isDisplay,
  paymentData,
}) => {
  const [themeId, tariffId] = paymentData;
  const [isLoading, setLoading] = useState<boolean>(false);
  const [eripCode, setEripCode] = useState<IErip>();

  const handlePay = async (erip?: boolean) => {
    const dataForOrder = {
      themeId,
      tariff: tariffId,
      erip,
    };

    setLoading(true);

    try {
      const orderDetails: IOrderDetails = await api.post(
        ENDPOINT.ORDERS,
        dataForOrder,
      );

      if (erip) {
        setEripCode({
          accountNumber: orderDetails.eripAccountNumber,
          invoiceNumber: orderDetails.eripInvoiceNumber,
          amountToPay: orderDetails.amountToPay,
          eripQRCodeLink: orderDetails.eripQRCodeLink,
        });

        setLoading(false);
        return;
      }

      // window.open(`${orderDetails.formUrl}`, '_blank');
      // window.location.reload();

      window.location.href = orderDetails.formUrl;
    } catch (err: any) {
      window.location.reload();
    }

    setLoading(false);
  };

  const handleClose = () => {
    setEripCode(undefined);
    onClose();
  };

  return (
    <Modal
      onClose={handleClose}
      display={isDisplay}
      title={
        eripCode
          ? `Оплатите счёт: ${eripCode.invoiceNumber}`
          : 'Выберите способ оплаты'
      }
    >
      {eripCode ? (
        <EripContent
          eripCode={eripCode}
          onClose={() => setEripCode(undefined)}
          onSuccess={() => window.location.reload()}
        />
      ) : (
        <PaymentContent isLoading={isLoading} onPay={handlePay} />
      )}
    </Modal>
  );
};

interface PaymentContentProps {
  isLoading: boolean;
  onPay: (erip: boolean) => void;
}

const PaymentContent: FC<PaymentContentProps> = ({ isLoading, onPay }) => {
  const [loadingType, setLoadingType] = useState<'erip' | 'card'>();

  const handlePay = (erip: boolean) => {
    setLoadingType(erip ? 'erip' : 'card');
    onPay(erip);
  };

  return (
    <div className={css.Container}>
      <div className={css.Wrapper}>
        <Button
          onClick={() => handlePay(false)}
          className={css.Button}
          type='primary'
          disabled={isLoading}
        >
          {isLoading && loadingType === 'card' ? <Spinner /> : 'Оплата картой'}
        </Button>

        <Button
          className={css.Button}
          onClick={() => window.open('https://forms.amocrm.ru/rvzdlmv')}
          disabled={isLoading}
        >
          Банковский перевод
        </Button>

        <Button
          className={css.Button}
          onClick={() => handlePay(true)}
          disabled={isLoading}
        >
          {isLoading && loadingType === 'erip' ? (
            <Spinner />
          ) : (
            'Оплата через ЕРИП'
          )}
        </Button>
      </div>
    </div>
  );
};

export interface EripProps {
  eripCode: IErip;
  onClose: () => void;
  onSuccess: () => void;
}

export const EripContent: FC<EripProps> = ({ eripCode, onClose, onSuccess }) => {
  const expiry = new Date();
  expiry.setHours(expiry.getHours() + 1);
  const formattedExpiry = expiry.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div className={css.Erip}>
      <div className={css.EripCard}>
        <div className={css.EripCardRow}>
          <span className={css.EripCardLabel}>Номер счёта</span>
          <span className={css.EripCardValue}>{eripCode.accountNumber}</span>
        </div>
        <div className={css.EripCardRow}>
          <span className={css.EripCardLabel}>Сумма</span>
          <span className={css.EripCardValue}>{eripCode.amountToPay} BYN</span>
        </div>
        <div className={css.EripCardRow}>
          <span className={css.EripCardLabel}>Счёт действует до</span>
          <span className={css.EripCardValue}>{formattedExpiry}</span>
        </div>
      </div>

      <ol className={css.EripSteps}>
        <li>Откройте любую систему оплаты ЕРИП (интернет-банк, банкомат, терминал).</li>
        <li>
          Перейдите в раздел: <b>Образование и развитие → Дополнительное образование →
          Профессиональное образование → Гродненская обл. → Регион. аген-во безоп-ти труда → Тестирование</b>.
          <br />Или найдите по коду услуги: <b>256125</b>.
        </li>
        <li>В поле «Номер счёта» введите <b>{eripCode.accountNumber}</b> и нажмите «Продолжить».</li>
        <li>Проверьте сумму — <b>{eripCode.amountToPay} BYN</b> — и подтвердите платёж.</li>
      </ol>

      <p className={css.EripLink}>
        Посмотреть счёт и инструкции{' '}
        <a href={eripCode.eripQRCodeLink} target='_blank' rel='noreferrer' className={css.Link}>
          можно по ссылке <SVGNewTab />
        </a>
      </p>

      <div className={css.Actions}>
        <Button onClick={onClose}>Отмена</Button>
        <Button type='primary' onClick={onSuccess}>Я оплатил</Button>
      </div>
    </div>
  );
};

export default PaymentModal;
